import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Play, Pause, ChevronRight, Plus, Undo2, ArrowLeft,
  Video, VideoOff, Copy, Check, Settings, Edit2, Trash2,
  X, Save, RefreshCw, Users, ChevronDown, ChevronUp, Home, Shield,
  Clock, MapPin, Eye, EyeOff, Image, QrCode, Camera, CameraOff,
  MonitorPlay, Wifi, WifiOff, Lock, Unlock, Smartphone, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import CourtStreamPanel from '../components/CourtStreamPanel';
import StreamShareModal from '../components/StreamShareModal';
import CameraPreview from '../components/CameraPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useIsMobile = () => {
  const check = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Phone: width < 768
    // 7-inch tablet landscape: width ~1024 but height < 700
    // iPad and larger: width >= 768 AND height >= 700 → desktop
    return w < 768 || (w < 1100 && h < 700);
  };
  const [isMobile, setIsMobile] = useState(check());
  useEffect(() => {
    const handler = () => setIsMobile(check());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// Format milliseconds to MM:SS
const formatTime = (ms) => {
  if (!ms && ms !== 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Parse MM:SS to milliseconds
const parseTime = (timeStr) => {
  const parts = timeStr.split(':');
  if (parts.length !== 2) return null;
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  if (isNaN(minutes) || isNaN(seconds)) return null;
  return (minutes * 60 + seconds) * 1000;
};

// ============== STAT BUTTON COMPONENT ==============
const StatButton = ({ label, color, onClick, size = 'normal' }) => {
  const baseClass = "font-bold rounded-xl transition-all active:scale-95 shadow-lg";
  const sizeClass = size === 'large' 
    ? "min-h-[70px] text-xl px-6 py-4" 
    : "min-h-[56px] text-lg px-4 py-3";
  
  const colorClasses = {
    green: "bg-emerald-600 hover:bg-emerald-500 text-white border-2 border-emerald-400",
    red: "bg-red-600 hover:bg-red-500 text-white border-2 border-red-400",
    blue: "bg-blue-600 hover:bg-blue-500 text-white border-2 border-blue-400",
    yellow: "bg-amber-500 hover:bg-amber-400 text-black border-2 border-amber-300",
    purple: "bg-purple-600 hover:bg-purple-500 text-white border-2 border-purple-400",
    gray: "bg-zinc-600 hover:bg-zinc-500 text-white border-2 border-zinc-400",
    orange: "bg-orange-500 hover:bg-orange-400 text-white border-2 border-orange-300",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClass} ${sizeClass} ${colorClasses[color] || colorClasses.gray}`}
    >
      {label}
    </button>
  );
};

// ============== PLAYER ROW COMPONENT (Mobile Optimized) ==============
const PlayerRow = ({ player, teamSide, teamColor, isSelected, onSelect, onEdit, onToggleCourt }) => {
  return (
    <div
      onClick={() => onSelect(player, teamSide)}
      className={`
        flex items-center justify-between px-4 py-4 cursor-pointer transition-all
        border-b border-zinc-700/50 active:bg-zinc-700/50
        ${isSelected 
          ? 'bg-blue-600/30 border-l-4' 
          : 'border-l-4 border-l-transparent'
        }
      `}
      style={{ borderLeftColor: isSelected ? teamColor : 'transparent' }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleCourt?.(player, teamSide); }}
          className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all shrink-0 ${
            player.on_court 
              ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
              : 'border-zinc-600 bg-transparent text-zinc-600'
          }`}
          title={player.on_court ? 'On Court - tap to bench' : 'On Bench - tap to sub in'}
          data-testid={`toggle-court-${player.player_id}`}
        >
          {player.on_court ? 'IN' : ''}
        </button>
        <div 
          className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white ${!player.on_court ? 'opacity-50' : ''}`}
          style={{ backgroundColor: teamColor || '#3B82F6' }}
        >
          {player.jersey_number}
        </div>
        <div>
          <span className={`text-lg font-semibold truncate max-w-[130px] block ${player.on_court ? 'text-white' : 'text-zinc-500'}`}>
            {player.name}
          </span>
          {!player.on_court && <span className="text-[10px] text-zinc-600 uppercase">Bench</span>}
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">{player.points || 0}</div>
          <div className="text-xs text-zinc-500 uppercase">PTS</div>
        </div>
        
        <div className="text-center">
          <div className={`text-xl font-bold ${(player.fouls || 0) >= 4 ? 'text-red-500' : 'text-amber-400'}`}>
            {player.fouls || 0}
          </div>
          <div className="text-xs text-zinc-500 uppercase">PF</div>
        </div>
        
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(player, teamSide); }}
          className="p-3 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-xl transition-all"
        >
          <Edit2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ============== STAT MODAL (Full Screen on Mobile) ==============
const StatModal = ({ player, teamSide, teamColor, onLogStat, onClose }) => {
  if (!player) return null;

  return (
    <div className="fixed inset-0 bg-zinc-950 z-50 flex flex-col">
      {/* Header */}
      <div 
        className="px-4 py-4 flex items-center justify-between safe-area-top"
        style={{ backgroundColor: teamColor + '40' }}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold text-white"
            style={{ backgroundColor: teamColor || '#3B82F6' }}
          >
            {player.jersey_number}
          </div>
          <div>
            <div className="text-xl font-bold text-white">{player.name}</div>
            <div className="text-sm text-zinc-400">{teamSide === 'home' ? 'Home' : 'Away'} Team</div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-3 hover:bg-zinc-800 rounded-full transition-all"
        >
          <X className="w-7 h-7 text-white" />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Scoring */}
        <div>
          <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Scoring</h3>
          <div className="grid grid-cols-3 gap-3">
            <StatButton label="+1 FT" color="green" size="large" onClick={() => onLogStat('ft', teamSide, player)} />
            <StatButton label="+2 PTS" color="green" size="large" onClick={() => onLogStat('fg', teamSide, player)} />
            <StatButton label="+3 PTS" color="green" size="large" onClick={() => onLogStat('fg3', teamSide, player)} />
          </div>
        </div>
        
        {/* Misses */}
        <div>
          <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Misses</h3>
          <div className="grid grid-cols-3 gap-3">
            <StatButton label="FT Miss" color="gray" onClick={() => onLogStat('ft_miss', teamSide, player)} />
            <StatButton label="2PT Miss" color="gray" onClick={() => onLogStat('fg_miss', teamSide, player)} />
            <StatButton label="3PT Miss" color="gray" onClick={() => onLogStat('fg3_miss', teamSide, player)} />
          </div>
        </div>

        {/* Other Stats */}
        <div>
          <h3 className="text-sm font-semibold text-zinc-400 uppercase mb-3">Other Stats</h3>
          <div className="grid grid-cols-4 gap-3">
            <StatButton label="AST" color="blue" onClick={() => onLogStat('ast', teamSide, player)} />
            <StatButton label="REB" color="purple" onClick={() => onLogStat('reb', teamSide, player)} />
            <StatButton label="STL" color="orange" onClick={() => onLogStat('stl', teamSide, player)} />
            <StatButton label="BLK" color="orange" onClick={() => onLogStat('blk', teamSide, player)} />
          </div>
        </div>

        {/* Foul & Turnover */}
        <div className="grid grid-cols-2 gap-3">
          <StatButton label="FOUL" color="red" size="large" onClick={() => onLogStat('foul', teamSide, player)} />
          <StatButton label="TURNOVER" color="yellow" size="large" onClick={() => onLogStat('to', teamSide, player)} />
        </div>
      </div>
    </div>
  );
};

// ============== EDIT PLAYER MODAL ==============
const EditPlayerModal = ({ player, teamSide, onSave, onDelete, onClose }) => {
  const [name, setName] = useState(player?.name || '');
  const [number, setNumber] = useState(player?.jersey_number || '');

  if (!player) return null;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Edit Player</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
            <X className="w-6 h-6 text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Jersey Number</label>
            <Input
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="bg-zinc-800 border-zinc-600 text-white text-xl h-14"
              placeholder="00"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Player Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-800 border-zinc-600 text-white text-lg h-14"
              placeholder="Player Name"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => onDelete(player, teamSide)}
              variant="destructive"
              className="flex-1 h-14 text-lg"
            >
              <Trash2 className="w-5 h-5 mr-2" /> Remove
            </Button>
            <Button
              onClick={() => onSave({ ...player, name, jersey_number: parseInt(number) }, teamSide)}
              className="flex-1 h-14 text-lg bg-emerald-600 hover:bg-emerald-500"
            >
              <Save className="w-5 h-5 mr-2" /> Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== STREAM SETTINGS MODAL ==============
const StreamSettingsModal = ({ streamSession, onCreateSession, onEndSession, onClose, copyToClipboard, gameId, authHeaders, onSessionUpdate }) => {
  const [customRtmpUrl, setCustomRtmpUrl] = useState('');
  const [customStreamKey, setCustomStreamKey] = useState('');
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [saving, setSaving] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Detect platform from RTMP URL
  const isYoutube = customRtmpUrl.includes('youtube.com') || customRtmpUrl.includes('rtmp.youtube');
  const isTwitch = customRtmpUrl.includes('twitch.tv');

  const saveCustomStream = async () => {
    if (!customRtmpUrl || !customStreamKey) {
      toast.error('Please enter both RTMP URL and Stream Key');
      return;
    }
    
    // For YouTube, require video ID
    if (isYoutube && !youtubeVideoId) {
      toast.error('Please enter your YouTube Video ID for playback');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/engine/stream/sessions?game_id=${gameId}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          custom_rtmp_url: customRtmpUrl,
          custom_stream_key: customStreamKey,
          playback_url: playbackUrl || null,
          platform: isYoutube ? 'youtube' : isTwitch ? 'twitch' : 'rtmp',
          youtube_video_id: youtubeVideoId || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Stream settings saved!');
        if (onSessionUpdate && data.session) {
          onSessionUpdate(data.session);
        }
        setCustomRtmpUrl('');
        setCustomStreamKey('');
        setPlaybackUrl('');
        setYoutubeVideoId('');
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save stream settings');
    }
    setSaving(false);
  };

  const setAsDefault = async () => {
    if (!streamSession?.session_id) {
      toast.error('No stream session to save as default');
      return;
    }
    
    setSettingDefault(true);
    try {
      const res = await fetch(`${API_URL}/api/engine/settings/default-stream/from-session/${streamSession.session_id}`, {
        method: 'POST',
        headers: authHeaders
      });
      
      if (res.ok) {
        toast.success('Stream settings saved as default for all new games!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save default settings');
      }
    } catch (err) {
      toast.error('Failed to save default settings');
    }
    setSettingDefault(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-white">Stream Settings</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
            <X className="w-6 h-6 text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Custom Stream Input Section */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Edit2 className="w-4 h-4" />
              Stream Configuration
            </h3>
            
            {/* Platform Quick Select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-zinc-400 mb-2">Streaming Platform</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setCustomRtmpUrl('rtmps://live.cloudflare.com:443/live/')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${customRtmpUrl.includes('cloudflare') ? 'bg-orange-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                >
                  ☁️ Cloudflare
                </button>
                <button
                  type="button"
                  onClick={() => setCustomRtmpUrl('rtmp://')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${customRtmpUrl && !customRtmpUrl.includes('cloudflare') && !customRtmpUrl.includes('youtube') && !customRtmpUrl.includes('twitch') ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                >
                  🔧 Custom RTMP
                </button>
                <button
                  type="button"
                  onClick={() => setCustomRtmpUrl('rtmp://a.rtmp.youtube.com/live2/')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${customRtmpUrl.includes('youtube') ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                >
                  ▶️ YouTube
                </button>
                <button
                  type="button"
                  onClick={() => setCustomRtmpUrl('rtmp://live.twitch.tv/app/')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${customRtmpUrl.includes('twitch') ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                >
                  🎮 Twitch
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">RTMP/RTMPS Server URL *</label>
                <Input
                  value={customRtmpUrl}
                  onChange={(e) => setCustomRtmpUrl(e.target.value)}
                  placeholder="rtmp://your-server.com/live or rtmps://..."
                  className="bg-zinc-900 border-zinc-600 text-white h-12 text-base"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Works with: Cloudflare, YouTube, Twitch, Restream, Vimeo, Facebook, or any RTMP server
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Stream Key *</label>
                <Input
                  value={customStreamKey}
                  onChange={(e) => setCustomStreamKey(e.target.value)}
                  placeholder="your-stream-key-from-platform"
                  className="bg-zinc-900 border-zinc-600 text-white h-12 text-base"
                />
                <p className="text-xs text-zinc-500 mt-1">Get this from your streaming platform's dashboard</p>
              </div>

              {/* YouTube Video ID - shown when YouTube RTMP detected */}
              {isYoutube && (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                  <label className="block text-sm font-medium text-red-400 mb-2">
                    YouTube Video ID * (Required for playback)
                  </label>
                  <Input
                    value={youtubeVideoId}
                    onChange={(e) => setYoutubeVideoId(e.target.value)}
                    placeholder="dQw4w9WgXcQ"
                    className="bg-zinc-900 border-red-600 text-white h-12 text-base"
                  />
                  <p className="text-xs text-red-300 mt-2">
                    Find this in your YouTube Studio → Go Live → copy the Video ID from your stream URL
                    <br />
                    Example: youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong> → ID is "dQw4w9WgXcQ"
                  </p>
                </div>
              )}

              {/* Playback URL - shown for non-YouTube platforms */}
              <div className="bg-cyan-900/20 border border-cyan-700 rounded-lg p-4">
                <label className="block text-sm font-medium text-cyan-400 mb-2">
                  📺 Playback URL (HLS/MP4) {!isYoutube && '*'}
                </label>
                <Input
                  value={playbackUrl}
                  onChange={(e) => setPlaybackUrl(e.target.value)}
                  placeholder="https://example.com/stream/manifest/video.m3u8"
                  className="bg-zinc-900 border-cyan-600 text-white h-12 text-base"
                />
                <p className="text-xs text-cyan-300 mt-2">
                  <strong>This is what viewers will watch.</strong> Get the HLS (.m3u8) or MP4 URL from your streaming provider.
                  <br />
                  • Cloudflare: customer-xxx.cloudflarestream.com/.../manifest/video.m3u8
                  <br />
                  • Vimeo: player.vimeo.com/external/xxx.m3u8
                  <br />
                  • Custom: Your CDN or media server URL
                </p>
              </div>

              <Button
                onClick={saveCustomStream}
                disabled={saving || !customRtmpUrl || !customStreamKey || (isYoutube && !youtubeVideoId)}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-lg font-semibold"
              >
                {saving ? 'Saving...' : '✓ Save Stream Configuration'}
              </Button>
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-700"></div>
            </div>
            <span className="relative bg-zinc-900 px-4 text-sm text-zinc-500">or use auto-generated</span>
          </div>

          {/* Current/Auto-generated Stream Section */}
          {!streamSession ? (
            <div className="text-center py-4">
              <Button
                onClick={onCreateSession}
                className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-500"
              >
                <Video className="w-6 h-6 mr-2" /> Generate Stream Key
              </Button>
              <p className="text-xs text-zinc-500 mt-2">Auto-generate RTMP URL and stream key</p>
            </div>
          ) : (
            <>
              <div className={`text-center py-3 rounded-xl text-lg font-bold ${
                streamSession.status === 'active' 
                  ? 'bg-red-900/50 text-red-400 animate-pulse' 
                  : 'bg-zinc-800 text-amber-400'
              }`}>
                {streamSession.status === 'active' ? '● LIVE NOW' : '◯ Waiting for stream...'}
                {streamSession.is_custom && <span className="ml-2 text-xs">(Custom)</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Current RTMP URL</label>
                <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                  <code className="text-sm text-emerald-400 flex-1 break-all">{streamSession.custom_rtmp_url || streamSession.rtmp_url}</code>
                  <button onClick={() => copyToClipboard(streamSession.custom_rtmp_url || streamSession.rtmp_url)} className="p-2 hover:bg-zinc-700 rounded-lg shrink-0">
                    <Copy className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Current Stream Key</label>
                <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                  <code className="text-sm text-amber-400 flex-1 break-all">{streamSession.custom_stream_key || streamSession.stream_key}</code>
                  <button onClick={() => copyToClipboard(streamSession.custom_stream_key || streamSession.stream_key)} className="p-2 hover:bg-zinc-700 rounded-lg shrink-0">
                    <Copy className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Full URL (for OBS/XBotGo)</label>
                <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                  <code className="text-sm text-blue-400 flex-1 break-all">{(streamSession.custom_rtmp_url || streamSession.rtmp_url)?.replace(/\/$/, '')}/{streamSession.custom_stream_key || streamSession.stream_key}</code>
                  <button onClick={() => copyToClipboard(`${(streamSession.custom_rtmp_url || streamSession.rtmp_url)?.replace(/\/$/, '')}/${streamSession.custom_stream_key || streamSession.stream_key}`)} className="p-2 hover:bg-zinc-700 rounded-lg shrink-0">
                    <Copy className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
              </div>

              {/* Playback URL Update Section */}
              <div className="border-t border-zinc-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-cyan-400 mb-2">
                  📺 Live Playback URL (HLS)
                </label>
                <div className="space-y-2">
                  <Input
                    value={playbackUrl || streamSession.playback_url || ''}
                    onChange={(e) => setPlaybackUrl(e.target.value)}
                    placeholder="https://customer-xxx.cloudflarestream.com/xxx/manifest/video.m3u8"
                    className="bg-zinc-900 border-zinc-600 text-white h-12 text-base"
                  />
                  <p className="text-xs text-zinc-500">
                    Paste the HLS (.m3u8) URL from Cloudflare Stream or your streaming provider for viewers to watch
                  </p>
                  <Button
                    onClick={async () => {
                      if (!playbackUrl) {
                        toast.error('Enter a playback URL');
                        return;
                      }
                      setSaving(true);
                      try {
                        const res = await fetch(`${API_URL}/api/engine/stream/sessions/${gameId}/playback-url`, {
                          method: 'PATCH',
                          headers: authHeaders,
                          body: JSON.stringify({ playback_url: playbackUrl })
                        });
                        if (res.ok) {
                          toast.success('Playback URL saved! Viewers can now watch.');
                          if (onSessionUpdate) {
                            const updated = { ...streamSession, playback_url: playbackUrl };
                            onSessionUpdate(updated);
                          }
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || 'Failed to save');
                        }
                      } catch (e) {
                        toast.error('Error saving playback URL');
                      }
                      setSaving(false);
                    }}
                    disabled={saving || !playbackUrl}
                    className="w-full h-12 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
                  >
                    {saving ? 'Saving...' : '💾 Save Playback URL'}
                  </Button>
                </div>
              </div>

              {/* OBS Overlay URL Section */}
              <div className="border-t border-zinc-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-green-400 mb-2">
                  OBS Browser Source Overlay
                </label>
                <div className="bg-zinc-900 rounded-lg p-3 flex items-center gap-2">
                  <code className="text-xs text-zinc-300 break-all flex-1">
                    {`${window.location.origin}/overlay/${gameId}`}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/overlay/${gameId}`)}
                    className="p-2 hover:bg-zinc-700 rounded-lg shrink-0"
                    data-testid="copy-overlay-url-btn"
                  >
                    <Copy className="w-4 h-4 text-green-400" />
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Add as Browser Source in OBS. Recommended: 1920x1080, custom CSS: body {"{"} background: transparent; {"}"}
                </p>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={setAsDefault} 
                  disabled={settingDefault}
                  variant="outline" 
                  className="flex-1 h-12 text-base border-amber-600 text-amber-400 hover:bg-amber-600/20"
                >
                  {settingDefault ? 'Saving...' : '⭐ Set as Default'}
                </Button>
                <Button onClick={onEndSession} variant="destructive" className="flex-1 h-12 text-base">
                  <VideoOff className="w-5 h-5 mr-2" /> End Session
                </Button>
              </div>

              {/* One-click repair for stuck/broken streams */}
              <div className="pt-3 mt-3 border-t border-zinc-800">
                <Button
                  onClick={async () => {
                    if (!window.confirm('Force this game LIVE and re-attach the Cloudflare stream? Use this if Cloudflare is receiving video but the platform viewer shows an error.')) return;
                    try {
                      const body = {};
                      if (streamSession?.cloudflare_uid) body.cloudflare_live_input_uid = streamSession.cloudflare_uid;
                      else if (streamSession?.custom_stream_key) body.stream_key = streamSession.custom_stream_key;
                      else if (streamSession?.playback_url) body.playback_url = streamSession.playback_url;
                      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/force-live`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...authHeaders },
                        body: JSON.stringify(body),
                      });
                      if (res.ok) {
                        toast.success('Game forced LIVE — stream re-attached');
                        onSessionUpdate && onSessionUpdate();
                      } else {
                        const err = await res.json();
                        toast.error(err.detail || 'Force-live failed');
                      }
                    } catch { toast.error('Force-live request failed'); }
                  }}
                  variant="outline"
                  className="w-full h-12 border-cyan-600 text-cyan-300 hover:bg-cyan-600/20"
                  data-testid="force-live-btn"
                >
                  🛠️ Force-Live &amp; Repair Stream
                </Button>
                <p className="text-xs text-zinc-500 text-center mt-1">
                  Use when Cloudflare is ingesting but the platform viewer shows &quot;stream error&quot;
                </p>
              </div>

              <p className="text-xs text-zinc-500 text-center">
                "Set as Default" will apply this stream configuration to all newly created games
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== GAME STREAM INLINE PANEL (Per-Game Mode) ==============
const GameStreamInlinePanel = ({ streamSession, onCreateSession, onSetupClick, onShareClick, gameName, gameId }) => {
  const [expanded, setExpanded] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const isLive = streamSession?.status === 'active';
  const rtmpUrl = streamSession?.custom_rtmp_url || streamSession?.rtmp_url;
  const streamKey = streamSession?.custom_stream_key || streamSession?.stream_key;

  return (
    <div className={`rounded-xl border transition-all ${isLive ? 'bg-red-950/30 border-red-800/60' : 'bg-zinc-900/80 border-zinc-700'}`} data-testid="game-stream-panel">
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center justify-between" data-testid="game-stream-toggle">
        <div className="flex items-center gap-3">
          <MonitorPlay className="w-5 h-5 text-cyan-400" />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">Stream</span>
              {isLive ? (
                <Badge className="bg-emerald-600/80 text-white text-[10px] px-1.5 py-0">
                  <Wifi className="w-3 h-3 mr-0.5" /> CONNECTED
                </Badge>
              ) : streamSession ? (
                <Badge variant="outline" className="text-zinc-500 border-zinc-600 text-[10px] px-1.5 py-0">
                  <WifiOff className="w-3 h-3 mr-0.5" /> WAITING
                </Badge>
              ) : (
                <Badge variant="outline" className="text-zinc-500 border-zinc-600 text-[10px] px-1.5 py-0">
                  NO STREAM
                </Badge>
              )}
            </div>
            <span className="text-xs text-zinc-500">Per-Game Mode</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-800/50">
          {!streamSession ? (
            <div className="pt-3 space-y-2">
              <p className="text-xs text-zinc-500">No stream session yet. Create one to get RTMP credentials.</p>
              <Button onClick={onCreateSession} className="w-full h-10 bg-cyan-700 hover:bg-cyan-600 text-white font-semibold" data-testid="create-stream-btn">
                <Play className="w-4 h-4 mr-2" />
                Create Stream Session
              </Button>
              <Button onClick={onSetupClick} variant="outline" className="w-full h-10 border-zinc-600 text-zinc-400 hover:text-white" data-testid="custom-stream-setup-btn">
                <Settings className="w-4 h-4 mr-2" />
                Custom RTMP / YouTube Setup
              </Button>
            </div>
          ) : (
            <>
              {/* Stream Credentials */}
              <div className="bg-zinc-800/60 rounded-lg p-3 space-y-2 mt-2">
                <div className="text-xs text-zinc-500 font-medium mb-1">STREAM CREDENTIALS</div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-zinc-500 shrink-0">RTMP:</span>
                  <code className="text-[11px] text-emerald-400 font-mono truncate flex-1">{rtmpUrl}</code>
                  <button onClick={() => copyToClipboard('rtmp', rtmpUrl)} className="p-1 hover:bg-zinc-700 rounded shrink-0" data-testid="copy-rtmp-url">
                    {copiedField === 'rtmp' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-zinc-400" />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-zinc-500 shrink-0">Key:</span>
                  <code className="text-[11px] text-amber-400 font-mono truncate flex-1">****{streamKey?.slice(-8)}</code>
                  <button onClick={() => copyToClipboard('key', streamKey)} className="p-1 hover:bg-zinc-700 rounded shrink-0" data-testid="copy-stream-key">
                    {copiedField === 'key' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-zinc-400" />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-zinc-500 shrink-0">Full:</span>
                  <code className="text-[11px] text-blue-400 font-mono truncate flex-1">{rtmpUrl?.replace(/\/$/, '')}/{streamKey}</code>
                  <button onClick={() => copyToClipboard('full', `${rtmpUrl?.replace(/\/$/, '')}/${streamKey}`)} className="p-1 hover:bg-zinc-700 rounded shrink-0" data-testid="copy-full-url">
                    {copiedField === 'full' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-zinc-400" />}
                  </button>
                </div>
              </div>

              {/* Share & Setup buttons */}
              <div className="flex gap-2">
                <Button onClick={onShareClick} className="flex-1 h-10 bg-cyan-700 hover:bg-cyan-600 text-white font-semibold" data-testid="share-stream-key-btn">
                  <QrCode className="w-4 h-4 mr-2" />
                  Share Stream Key
                </Button>
                <Button onClick={onSetupClick} variant="outline" className="h-10 px-3 border-zinc-600 text-zinc-400 hover:text-white" data-testid="stream-settings-btn">
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* OBS Overlay URL - Always visible when expanded */}
      {expanded && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between gap-2 bg-green-950/30 border border-green-800/40 rounded-lg p-2">
            <span className="text-[11px] text-green-400 font-medium shrink-0">OBS Overlay:</span>
            <code className="text-[10px] text-green-300 font-mono truncate flex-1">/overlay/{gameId}</code>
            <button onClick={() => copyToClipboard('overlay', `${window.location.origin}/overlay/${gameId}`)} className="p-1 hover:bg-zinc-700 rounded shrink-0" data-testid="copy-overlay-url-inline">
              {copiedField === 'overlay' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 text-green-400" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== MAIN COMPONENT ==============
export default function OperatorConsole() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const wsRef = useRef(null);
  
  // Game state
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  
  // Local clock display (for smooth countdown)
  const [displayTime, setDisplayTime] = useState(0);
  
  // Rosters
  const [homeRoster, setHomeRoster] = useState([]);
  const [awayRoster, setAwayRoster] = useState([]);
  
  // Play-by-play
  const [playByPlay, setPlayByPlay] = useState([]);
  
  // Stream session
  const [streamSession, setStreamSession] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('home'); // 'home' | 'away' | 'log'
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTeamSide, setSelectedTeamSide] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingTeamSide, setEditingTeamSide] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(null);
  const [bulkRosterText, setBulkRosterText] = useState('');
  const [showScoreEdit, setShowScoreEdit] = useState(false);
  const [editHomeScore, setEditHomeScore] = useState('');
  const [editAwayScore, setEditAwayScore] = useState('');
  const [showStreamSetup, setShowStreamSetup] = useState(false);
  const [showClockSettings, setShowClockSettings] = useState(false);
  const [clockInput, setClockInput] = useState('');
  const [showLocationSettings, setShowLocationSettings] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [periodInput, setPeriodInput] = useState('');
  const [homeTimeoutsInput, setHomeTimeoutsInput] = useState('');
  const [awayTimeoutsInput, setAwayTimeoutsInput] = useState('');
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [homeTeamNameInput, setHomeTeamNameInput] = useState('');
  const [awayTeamNameInput, setAwayTeamNameInput] = useState('');
  const [homeTeamColorInput, setHomeTeamColorInput] = useState('');
  const [awayTeamColorInput, setAwayTeamColorInput] = useState('');
  const [showOverlaySettings, setShowOverlaySettings] = useState(false);
  const [showStreamShareModal, setShowStreamShareModal] = useState(false);
  const [showPerGamePreview, setShowPerGamePreview] = useState(false);
  const [freePreview, setFreePreview] = useState(false);
  const [scoringMode, setScoringMode] = useState('quick');
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [overlaySettings, setOverlaySettings] = useState({
    showWatermark: false,  // Default OFF — operator can flip on per-game
    watermarkPosition: 'top-right',
    watermarkOpacity: 0.6
  });
  // Auth headers
  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const isGameFinal = game?.status === 'final';
  const isMobile = useIsMobile();
  const [mobileSection, setMobileSection] = useState('stream'); // 'stream' | 'score' | 'roster'
  const [forceMobile, setForceMobile] = useState(null); // null = auto-detect, true/false = override

  // Fetch game data
  const fetchGame = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`);
      const data = await res.json();
      setGame(data);
      setFreePreview(!!data.free_preview);
      return data;
    } catch (err) {
      toast.error('Failed to load game');
      return null;
    }
  }, [gameId]);

  const toggleFreePreview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}/free-preview`, {
        method: 'PUT', headers: authHeaders
      });
      const data = await res.json();
      setFreePreview(data.free_preview);
      toast.success(data.message);
    } catch { toast.error('Failed to toggle'); }
  };

  // Fetch rosters
  const fetchRosters = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/rosters`);
      const data = await res.json();
      
      data.forEach(roster => {
        if (roster.team_name === game?.home_team_name || roster.team_id === 'home') {
          setHomeRoster(roster.players || []);
        } else {
          setAwayRoster(roster.players || []);
        }
      });
    } catch (err) {
      console.error('Failed to load rosters');
    }
  }, [gameId, game?.home_team_name]);

  // Fetch play-by-play
  const fetchPlayByPlay = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/play-by-play?limit=50`);
      const data = await res.json();
      setPlayByPlay(data);
    } catch (err) {
      console.error('Failed to load play-by-play');
    }
  }, [gameId]);

  // Fetch computed stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/stats`);
      const data = await res.json();
      
      if (data.player_stats) {
        const statsMap = {};
        data.player_stats.forEach(ps => {
          statsMap[ps.player_id] = ps;
        });
        
        setHomeRoster(prev => prev.map(p => ({
          ...p,
          points: statsMap[p.player_id]?.PTS || 0,
          fouls: statsMap[p.player_id]?.PF || 0,
        })));
        
        setAwayRoster(prev => prev.map(p => ({
          ...p,
          points: statsMap[p.player_id]?.PTS || 0,
          fouls: statsMap[p.player_id]?.PF || 0,
        })));
      }
      
      if (data.score) {
        setGame(prev => prev ? { ...prev, score: data.score } : prev);
      }
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, [gameId]);

  // Fetch stream session
  const fetchStreamSession = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/stream/sessions/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        // Unwrap the session object if nested
        const session = data.session || data;
        setStreamSession(session);
      }
    } catch (err) {
      console.error('Failed to fetch stream session:', err);
    }
  }, [gameId]);

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = `${API_URL.replace('http', 'ws')}/api/engine/ws/game/${gameId}?token=${token}`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => setConnected(true);
      
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'stat') {
            if (msg.score) setGame(prev => prev ? { ...prev, score: msg.score } : prev);
            fetchPlayByPlay();
            fetchStats();
          } else if (msg.type === 'clock') {
            setGame(prev => prev ? { ...prev, clock: msg.clock } : prev);
          } else if (msg.type === 'status') {
            setGame(prev => prev ? { ...prev, status: msg.status } : prev);
          } else if (msg.type === 'stream_status') {
            fetchStreamSession();
          }
        } catch (err) {
          console.debug('WebSocket received non-JSON message');
        }
      };
      
      ws.onclose = () => {
        setConnected(false);
        setTimeout(connectWebSocket, 3000);
      };
      
      ws.onerror = () => setConnected(false);
      wsRef.current = ws;
    };
    
    connectWebSocket();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [gameId, token, fetchPlayByPlay, fetchStats, fetchStreamSession]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchGame();
      setLoading(false);
    };
    loadData();
  }, [fetchGame]);

  useEffect(() => {
    if (game) {
      fetchRosters();
      fetchPlayByPlay();
      fetchStats();
      fetchStreamSession();
    }
  }, [game?.game_id, fetchRosters, fetchPlayByPlay, fetchStats, fetchStreamSession]);

  // Local clock countdown timer
  useEffect(() => {
    if (!game?.clock) return;
    
    const clock = game.clock;
    
    // Calculate current time
    let currentTime = clock.time_remaining_ms || 0;
    
    if (clock.is_running && clock.last_start_time) {
      const lastStart = new Date(clock.last_start_time).getTime();
      const now = Date.now();
      const elapsed = now - lastStart;
      currentTime = Math.max(0, (clock.time_remaining_ms || 0) - elapsed);
    }
    
    setDisplayTime(currentTime);
    
    // If clock is running, start interval
    if (clock.is_running) {
      const interval = setInterval(() => {
        setDisplayTime(prev => Math.max(0, prev - 100));
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [game?.clock]);

  // Stat type mapping
  const STAT_TYPE_MAP = {
    'ft': { event_type: 'FT', event_subtype: 'FT_MADE' },
    'ft_miss': { event_type: 'FT', event_subtype: 'FT_MISS' },
    'fg': { event_type: 'SHOT', event_subtype: 'FG2_MADE' },
    'fg_miss': { event_type: 'SHOT', event_subtype: 'FG2_MISS' },
    'fg3': { event_type: 'SHOT', event_subtype: 'FG3_MADE' },
    'fg3_miss': { event_type: 'SHOT', event_subtype: 'FG3_MISS' },
    'foul': { event_type: 'FOUL', event_subtype: 'FOUL' },
    'ast': { event_type: 'AST', event_subtype: 'AST' },
    'reb': { event_type: 'REB', event_subtype: 'REB' },
    'stl': { event_type: 'STL', event_subtype: 'STL' },
    'blk': { event_type: 'BLK', event_subtype: 'BLK' },
    'to': { event_type: 'TO', event_subtype: 'TO' }
  };

  const logStat = async (statType, teamSide, player = null) => {
    try {
      const mapping = STAT_TYPE_MAP[statType];
      if (!mapping) return;
      
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/stat`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_side: teamSide,
          player_id: player?.player_id,
          player_name: player?.name,
          player_number: player?.jersey_number,
          event_type: mapping.event_type,
          event_subtype: mapping.event_subtype,
          client: 'operator_ipad'
        })
      });
      
      if (res.ok) {
        setSelectedPlayer(null);
        setSelectedTeamSide(null);
        toast.success(`${mapping.event_subtype}`);
        fetchGame();
      } else {
        toast.error('Failed to log stat');
      }
    } catch (err) {
      toast.error('Failed to log stat');
    }
  };

  const undoLastStat = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/undo`, {
        method: 'POST',
        headers: authHeaders
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Undone #${data.reverted_seq}`);
        fetchGame();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Nothing to undo');
      }
    } catch (err) {
      toast.error('Failed to undo');
    }
  };

  const generatePlayerId = () => `player_${Math.random().toString(36).substr(2, 9)}`;

  // Toggle player on/off court (substitution)
  const togglePlayerCourt = async (player, teamSide) => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/substitution`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_id: teamSide,
          ...(player.on_court 
            ? { player_out_id: player.player_id } 
            : { player_in_id: player.player_id })
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const updatedPlayers = data.players || [];
        if (teamSide === 'home') setHomeRoster(updatedPlayers);
        else setAwayRoster(updatedPlayers);
        toast.success(player.on_court ? `${player.name} to bench` : `${player.name} subbed in`);
      } else {
        toast.error('Substitution failed');
      }
    } catch (err) {
      toast.error('Substitution failed');
    }
  };

  // Team-level scoring (no specific player)
  const logTeamStat = async (statType, teamSide) => {
    try {
      const mapping = STAT_TYPE_MAP[statType];
      if (!mapping) return;
      
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/stat`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_side: teamSide,
          player_id: null,
          player_name: 'Team',
          event_type: mapping.event_type,
          event_subtype: mapping.event_subtype,
          client: 'operator_ipad'
        })
      });
      
      if (res.ok) {
        toast.success(`Team ${mapping.event_subtype}`);
        fetchGame();
      } else {
        toast.error('Failed to log team stat');
      }
    } catch (err) {
      toast.error('Failed to log team stat');
    }
  };

  const openScoreEdit = () => {
    setEditHomeScore(String(game.score?.home || 0));
    setEditAwayScore(String(game.score?.away || 0));
    setShowScoreEdit(true);
  };

  const saveScoreEdit = async () => {
    const home = parseInt(editHomeScore);
    const away = parseInt(editAwayScore);
    if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
      toast.error('Enter valid scores');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/score/set`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ home, away })
      });
      if (res.ok) {
        toast.success(`Score set to ${home}-${away}`);
        setShowScoreEdit(false);
        fetchGame();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to set score');
      }
    } catch {
      toast.error('Failed to set score');
    }
  };

  const openBulkRoster = (teamSide) => {
    const currentRoster = teamSide === 'home' ? homeRoster : awayRoster;
    const text = currentRoster.map(p => `${p.jersey_number || 0} ${p.name}`).join('\n');
    setBulkRosterText(text);
    setShowAddPlayer(teamSide);
  };

  const saveBulkRoster = async (teamSide) => {
    const lines = bulkRosterText.split('\n').filter(l => l.trim());
    const players = lines.map(line => {
      const m = line.match(/^#?(\d+)\s+(.+)$/);
      if (m) return { player_id: generatePlayerId(), name: m[2].trim(), jersey_number: parseInt(m[1]), is_starter: false, stats: {} };
      const m2 = line.match(/^(.+?)\s+#?(\d+)$/);
      if (m2) return { player_id: generatePlayerId(), name: m2[1].trim(), jersey_number: parseInt(m2[2]), is_starter: false, stats: {} };
      return { player_id: generatePlayerId(), name: line.trim(), jersey_number: 0, is_starter: false, stats: {} };
    }).filter(p => p.name);

    try {
      await fetch(`${API_URL}/api/engine/games/${gameId}/roster`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_id: teamSide,
          team_name: teamSide === 'home' ? game.home_team_name : game.away_team_name,
          players
        })
      });
      if (teamSide === 'home') setHomeRoster(players);
      else setAwayRoster(players);
      setShowAddPlayer(null);
      setBulkRosterText('');
      toast.success(`Roster saved — ${players.length} players`);
    } catch {
      toast.error('Failed to save roster');
    }
  };

  const savePlayer = async (updatedPlayer, teamSide) => {
    const currentRoster = teamSide === 'home' ? homeRoster : awayRoster;
    const updatedPlayers = currentRoster.map(p => 
      p.player_id === updatedPlayer.player_id ? updatedPlayer : p
    );

    try {
      await fetch(`${API_URL}/api/engine/games/${gameId}/roster`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_id: teamSide,
          team_name: teamSide === 'home' ? game.home_team_name : game.away_team_name,
          players: updatedPlayers
        })
      });

      if (teamSide === 'home') setHomeRoster(updatedPlayers);
      else setAwayRoster(updatedPlayers);

      setEditingPlayer(null);
      toast.success('Player updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const deletePlayer = async (player, teamSide) => {
    const currentRoster = teamSide === 'home' ? homeRoster : awayRoster;
    const updatedPlayers = currentRoster.filter(p => p.player_id !== player.player_id);

    try {
      await fetch(`${API_URL}/api/engine/games/${gameId}/roster`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          team_id: teamSide,
          team_name: teamSide === 'home' ? game.home_team_name : game.away_team_name,
          players: updatedPlayers
        })
      });

      if (teamSide === 'home') setHomeRoster(updatedPlayers);
      else setAwayRoster(updatedPlayers);

      setEditingPlayer(null);
      toast.success('Player removed');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  // Clock controls
  const startClock = () => fetch(`${API_URL}/api/engine/games/${gameId}/clock/start`, { method: 'POST', headers: authHeaders });
  const stopClock = () => fetch(`${API_URL}/api/engine/games/${gameId}/clock/stop`, { method: 'POST', headers: authHeaders });
  const nextPeriod = () => fetch(`${API_URL}/api/engine/games/${gameId}/period/next`, { method: 'POST', headers: authHeaders });
  
  const setClock = async () => {
    const ms = parseTime(clockInput);
    if (ms === null) { toast.error('Invalid format'); return; }
    await fetch(`${API_URL}/api/engine/games/${gameId}/clock/set`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ time_remaining_ms: ms })
    });
    setClockInput('');
    setShowClockSettings(false);
  };

  const updateStatus = (status) => fetch(`${API_URL}/api/engine/games/${gameId}/status`, {
    method: 'PUT', headers: authHeaders, body: JSON.stringify({ status })
  });

  // Timeout controls
  const callTimeout = async (teamSide) => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/timeout?team_side=${teamSide}`, {
        method: 'POST',
        headers: authHeaders
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${teamSide === 'home' ? 'Home' : 'Away'} Timeout - ${data.timeouts_remaining} remaining`);
        // Refresh game data to get updated timeout count
        fetchGame();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'No timeouts remaining');
      }
    } catch (err) {
      toast.error('Failed to call timeout');
    }
  };

  // Location update
  const updateLocation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ location: locationInput })
      });
      
      if (res.ok) {
        toast.success('Location updated');
        fetchGame();
        setShowLocationSettings(false);
      } else {
        toast.error('Failed to update location');
      }
    } catch (err) {
      toast.error('Failed to update location');
    }
  };

  // Team settings update
  const updateTeamSettings = async () => {
    try {
      const updateData = {};
      if (homeTeamNameInput) updateData.home_team_name = homeTeamNameInput;
      if (awayTeamNameInput) updateData.away_team_name = awayTeamNameInput;
      if (homeTeamColorInput) updateData.home_team_color = homeTeamColorInput;
      if (awayTeamColorInput) updateData.away_team_color = awayTeamColorInput;
      
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success('Team settings updated');
        fetchGame();
        setShowTeamSettings(false);
      } else {
        toast.error('Failed to update team settings');
      }
    } catch (err) {
      toast.error('Failed to update team settings');
    }
  };

  // Save overlay settings to game
  const saveOverlaySettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          overlay_settings: overlaySettings
        })
      });
      
      if (res.ok) {
        toast.success('Overlay settings saved');
        setShowOverlaySettings(false);
        // Broadcast via WebSocket so viewers get updated
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'OVERLAY_SETTINGS_UPDATED',
            settings: overlaySettings
          }));
        }
      } else {
        toast.error('Failed to save overlay settings');
      }
    } catch (err) {
      toast.error('Failed to save overlay settings');
    }
  };

  // Stream controls
  const createStreamSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/stream/sessions?game_id=${gameId}`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      setStreamSession(data.session);
      toast.success('Stream created');
    } catch (err) { toast.error('Failed'); }
  };

  const endStreamSession = async () => {
    if (!streamSession) return;
    await fetch(`${API_URL}/api/engine/stream/sessions/${streamSession.session_id}/end`, { method: 'PUT', headers: authHeaders });
    setStreamSession(null);
    toast.success('Stream ended');
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied!');
    } catch (err) { toast.error('Failed to copy'); }
  };

  const calculateTeamFouls = (roster) => roster.reduce((sum, p) => sum + (p.fouls || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-red-500 text-xl">Game not found</div>
      </div>
    );
  }

  const currentRoster = activeTab === 'home' ? homeRoster : awayRoster;
  const currentTeamColor = activeTab === 'home' ? (game.home_team_color || '#3B82F6') : (game.away_team_color || '#EF4444');
  const showMobile = forceMobile !== null ? forceMobile : isMobile;

  /* ===== MOBILE COMMAND MODE ===== */
  if (showMobile) {
    const rtmpUrl = streamSession?.custom_rtmp_url || streamSession?.rtmp_url;
    const streamKey = streamSession?.custom_stream_key || streamSession?.stream_key;
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex flex-col select-none" data-testid="mobile-command-mode">
        {/* Mobile Header */}
        <div className="bg-zinc-900 border-b border-zinc-800 px-3 py-2 flex items-center justify-between safe-area-top">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/operator')} className="p-1.5 hover:bg-zinc-800 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="text-xs font-bold text-white leading-tight">{game.home_team_name} vs {game.away_team_name}</p>
              <p className="text-[10px] text-zinc-500">{game.location || 'No location'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] px-2 ${
              game.status === 'live' ? 'bg-red-600 text-white' : 
              game.status === 'warmup' ? 'bg-amber-600 text-white' : 
              game.status === 'halftime' ? 'bg-blue-600 text-white' : 
              game.status === 'final' ? 'bg-zinc-600 text-white' : 'bg-zinc-700 text-zinc-300'
            }`} data-testid="mobile-status-badge">
              {game.status === 'live' && <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse inline-block" />}
              {game.status}
            </Badge>
            <button onClick={() => setForceMobile(false)} className="p-1.5 bg-zinc-800 rounded-lg" title="Switch to tablet view">
              <Monitor className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Score Banner — tap to edit */}
        <div onClick={openScoreEdit} className="bg-zinc-900/50 border-b border-zinc-800 px-4 py-3 flex items-center justify-between cursor-pointer active:bg-zinc-800/50" data-testid="mobile-score-banner">
          <div className="text-center flex-1">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider truncate">{game.home_team_name}</p>
            <p className="text-3xl font-black tabular-nums" style={{ color: game.home_team_color || '#3B82F6' }}>{game.score?.home || 0}</p>
          </div>
          <div className="text-center px-3">
            <p className="text-xs text-zinc-600 font-mono">P{game.clock?.period || 1}</p>
            <p className="text-sm font-mono text-zinc-400">{formatTime(game.clock?.time_remaining_ms)}</p>
            <p className="text-[8px] text-zinc-600 mt-0.5">TAP TO EDIT</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider truncate">{game.away_team_name}</p>
            <p className="text-3xl font-black tabular-nums" style={{ color: game.away_team_color || '#EF4444' }}>{game.score?.away || 0}</p>
          </div>
        </div>

        {/* Status Buttons — mobile-friendly wrap on narrow screens */}
        <div className="bg-zinc-900 border-b border-zinc-800 px-2 py-2 flex flex-wrap gap-1.5" data-testid="mobile-status-controls">
          {['scheduled', 'warmup', 'live', 'halftime'].map(s => (
            <button
              key={s}
              onClick={() => { if (!isGameFinal) updateStatus(s); }}
              disabled={isGameFinal}
              className={`flex-1 min-w-[60px] h-10 rounded-lg text-[11px] font-bold capitalize transition-all ${
                game.status === s
                  ? s === 'live' ? 'bg-red-600 text-white' : s === 'warmup' ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'
                  : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
              } ${isGameFinal ? 'opacity-30' : ''}`}
              data-testid={`mobile-status-${s}`}
            >
              {s}
            </button>
          ))}
          {!isGameFinal && (
            <button
              onClick={() => setShowFinalConfirm(true)}
              className="flex-1 min-w-[60px] h-10 px-2 rounded-lg text-[11px] font-bold bg-red-900/50 text-red-400 border border-red-700/40"
              data-testid="mobile-end-game"
            >
              End Game
            </button>
          )}
        </div>

        {/* Section Tabs */}
        <div className="bg-zinc-900 border-b border-zinc-800 px-3 py-1.5 flex gap-1">
          {[
            { id: 'stream', label: 'Stream', icon: Video },
            { id: 'score', label: 'Score', icon: Shield },
            { id: 'roster', label: 'Roster', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setMobileSection(tab.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                mobileSection === tab.id ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`mobile-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div className="flex-1 overflow-y-auto">
          {mobileSection === 'stream' && (
            <div className="p-3 space-y-3" data-testid="mobile-stream-section">
              {/* Stream Status */}
              <div className={`rounded-xl p-3 border ${streamSession ? 'bg-emerald-950/30 border-emerald-700/40' : 'bg-red-950/30 border-red-700/40'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {streamSession ? <Wifi className="w-4 h-4 text-emerald-400" /> : <WifiOff className="w-4 h-4 text-red-400" />}
                    <span className="text-sm font-bold">{streamSession ? 'Stream Active' : 'No Active Stream'}</span>
                  </div>
                  {!streamSession ? (
                    <Button onClick={createStreamSession} size="sm" className="h-7 text-xs bg-emerald-600">
                      Start Stream
                    </Button>
                  ) : (
                    <Button onClick={() => setShowStreamSetup(true)} size="sm" className="h-7 text-xs bg-zinc-700">
                      <Settings className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                </div>
                {streamSession && (
                  <div className="text-[10px] text-zinc-500 flex items-center gap-2">
                    <span>ID: {streamSession.session_id?.slice(0, 8)}...</span>
                    {streamSession.is_custom && <Badge className="bg-amber-700/30 text-amber-400 text-[9px] px-1">Custom</Badge>}
                  </div>
                )}
              </div>

              {/* RTMP Credentials */}
              {streamSession && (
                <div className="space-y-2">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">RTMP URL</span>
                      <button onClick={() => copyToClipboard(rtmpUrl)} className="p-1 hover:bg-zinc-800 rounded">
                        <Copy className="w-3.5 h-3.5 text-zinc-400" />
                      </button>
                    </div>
                    <code className="text-xs text-emerald-400 break-all block" data-testid="mobile-rtmp-url">{rtmpUrl}</code>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">Stream Key</span>
                      <button onClick={() => copyToClipboard(streamKey)} className="p-1 hover:bg-zinc-800 rounded">
                        <Copy className="w-3.5 h-3.5 text-zinc-400" />
                      </button>
                    </div>
                    <code className="text-xs text-amber-400 break-all block" data-testid="mobile-stream-key">{streamKey}</code>
                  </div>

                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">Full URL (OBS/XBotGo)</span>
                      <button onClick={() => copyToClipboard(`${rtmpUrl?.replace(/\/$/, '')}/${streamKey}`)} className="p-1 hover:bg-zinc-800 rounded">
                        <Copy className="w-3.5 h-3.5 text-zinc-400" />
                      </button>
                    </div>
                    <code className="text-xs text-blue-400 break-all block">{rtmpUrl?.replace(/\/$/, '')}/{streamKey}</code>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setShowStreamShareModal(true)}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-xs font-medium"
                      data-testid="mobile-share-stream"
                    >
                      <QrCode className="w-3.5 h-3.5" /> Share Stream
                    </button>
                    <button
                      onClick={() => setShowStreamSetup(true)}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-xl text-xs font-medium"
                      data-testid="mobile-edit-stream"
                    >
                      <Settings className="w-3.5 h-3.5" /> Stream Settings
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {mobileSection === 'score' && (
            <div className="p-3 space-y-2" data-testid="mobile-score-section">
              {game.sport === 'soccer' ? (
                /* ===== SOCCER SCORING ===== */
                <div className="grid grid-cols-2 gap-2">
                  {['home', 'away'].map((side) => {
                    const name = side === 'home' ? game.home_team_name : game.away_team_name;
                    const color = side === 'home' ? game.home_team_color : game.away_team_color;
                    return (
                      <div key={side} className="bg-zinc-900 border border-zinc-800 rounded-xl p-2" data-testid={`mobile-${side}-soccer`}>
                        <p className="text-xs font-bold mb-1.5 truncate" style={{ color: color || (side === 'home' ? '#3B82F6' : '#EF4444') }}>{name}</p>
                        <button
                          onClick={() => logTeamStat('goal', side)}
                          disabled={isGameFinal}
                          className="w-full h-20 rounded-lg bg-emerald-600 active:scale-95 text-white font-black text-2xl mb-1.5 disabled:opacity-30 shadow-lg shadow-emerald-600/30"
                          data-testid={`mobile-${side}-goal`}
                        >
                          GOAL
                        </button>
                        <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                          <button
                            onClick={() => logTeamStat('yellow_card', side)}
                            disabled={isGameFinal}
                            className="h-10 rounded-lg bg-yellow-500 active:scale-95 text-black text-[10px] font-bold disabled:opacity-30"
                            data-testid={`mobile-${side}-yellow`}
                          >
                            YELLOW
                          </button>
                          <button
                            onClick={() => logTeamStat('red_card', side)}
                            disabled={isGameFinal}
                            className="h-10 rounded-lg bg-red-600 active:scale-95 text-white text-[10px] font-bold disabled:opacity-30"
                            data-testid={`mobile-${side}-red`}
                          >
                            RED
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            onClick={() => logTeamStat('shot_on', side)}
                            disabled={isGameFinal}
                            className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30"
                            data-testid={`mobile-${side}-shot`}
                          >
                            SHOT
                          </button>
                          <button
                            onClick={() => logTeamStat('corner', side)}
                            disabled={isGameFinal}
                            className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30"
                            data-testid={`mobile-${side}-corner`}
                          >
                            CORNER
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ===== BASKETBALL SCORING (default) ===== */
                <div className="grid grid-cols-2 gap-2">
                {/* Home Team Scoring */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
                  <p className="text-xs font-bold mb-1.5 truncate" style={{ color: game.home_team_color || '#3B82F6' }}>{game.home_team_name}</p>
                  <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                    <button onClick={() => logTeamStat('ft', 'home')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-home-ft">FT</button>
                    <button onClick={() => logTeamStat('fg', 'home')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-home-2pt">+2</button>
                    <button onClick={() => logTeamStat('fg3', 'home')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-home-3pt">+3</button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button onClick={() => logTeamStat('foul', 'home')} disabled={isGameFinal} className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30" data-testid="mobile-home-foul">FOUL ({game.fouls?.home || 0})</button>
                    <button onClick={() => callTimeout('home')} disabled={isGameFinal} className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30" data-testid="mobile-home-to">T/O ({game.timeouts?.home ?? 5})</button>
                  </div>
                </div>

                {/* Away Team Scoring */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
                  <p className="text-xs font-bold mb-1.5 truncate" style={{ color: game.away_team_color || '#EF4444' }}>{game.away_team_name}</p>
                  <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                    <button onClick={() => logTeamStat('ft', 'away')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-away-ft">FT</button>
                    <button onClick={() => logTeamStat('fg', 'away')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-away-2pt">+2</button>
                    <button onClick={() => logTeamStat('fg3', 'away')} disabled={isGameFinal} className="h-14 rounded-lg bg-zinc-800 active:scale-95 text-white font-black text-xl border border-zinc-700 disabled:opacity-30" data-testid="mobile-away-3pt">+3</button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button onClick={() => logTeamStat('foul', 'away')} disabled={isGameFinal} className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30" data-testid="mobile-away-foul">FOUL ({game.fouls?.away || 0})</button>
                    <button onClick={() => callTimeout('away')} disabled={isGameFinal} className="h-10 rounded-lg bg-zinc-800 active:scale-95 text-white text-[10px] font-bold border border-zinc-700 disabled:opacity-30" data-testid="mobile-away-to">T/O ({game.timeouts?.away ?? 5})</button>
                  </div>
                </div>
                </div>
              )}

              {/* Undo */}
              <button onClick={undoLastStat} className="w-full py-2.5 rounded-xl bg-amber-700/30 text-amber-400 text-xs font-bold border border-amber-700/40" data-testid="mobile-undo">
                <Undo2 className="w-3.5 h-3.5 inline mr-1.5" /> Undo Last Play
              </button>
            </div>
          )}

          {mobileSection === 'roster' && (
            <div className="p-3 space-y-3" data-testid="mobile-roster-section">
              {/* Home Roster */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between" style={{ borderLeftWidth: 3, borderLeftColor: game.home_team_color || '#3B82F6' }}>
                  <span className="text-xs font-bold text-white">{game.home_team_name} ({homeRoster.length})</span>
                  <button onClick={() => openBulkRoster('home')} className="text-[10px] font-bold text-emerald-400" data-testid="mobile-add-home-player">
                    <Plus className="w-3 h-3 inline" /> Edit
                  </button>
                </div>
                {homeRoster.length === 0 ? (
                  <p className="text-center py-4 text-zinc-600 text-xs">No players</p>
                ) : (
                  <div className="divide-y divide-zinc-800/50 max-h-[180px] overflow-y-auto">
                    {homeRoster.map(p => (
                      <div key={p.player_id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 font-mono w-6">{p.jersey_number}</span>
                          <span className="text-white">{p.name}</span>
                        </div>
                        <span className="text-zinc-400 tabular-nums">{p.stats?.points || 0} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Away Roster */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between" style={{ borderLeftWidth: 3, borderLeftColor: game.away_team_color || '#EF4444' }}>
                  <span className="text-xs font-bold text-white">{game.away_team_name} ({awayRoster.length})</span>
                  <button onClick={() => openBulkRoster('away')} className="text-[10px] font-bold text-emerald-400" data-testid="mobile-add-away-player">
                    <Plus className="w-3 h-3 inline" /> Edit
                  </button>
                </div>
                {awayRoster.length === 0 ? (
                  <p className="text-center py-4 text-zinc-600 text-xs">No players</p>
                ) : (
                  <div className="divide-y divide-zinc-800/50 max-h-[180px] overflow-y-auto">
                    {awayRoster.map(p => (
                      <div key={p.player_id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-500 font-mono w-6">{p.jersey_number}</span>
                          <span className="text-white">{p.name}</span>
                        </div>
                        <span className="text-zinc-400 tabular-nums">{p.stats?.points || 0} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mobile Modals — reuse existing */}
        {showStreamSetup && (
          <StreamSettingsModal
            streamSession={streamSession}
            onCreateSession={createStreamSession}
            onEndSession={endStreamSession}
            onClose={() => setShowStreamSetup(false)}
            copyToClipboard={copyToClipboard}
            gameId={gameId}
            authHeaders={authHeaders}
            onSessionUpdate={(session) => { setStreamSession(session); fetchGame(); }}
          />
        )}
        {showStreamShareModal && streamSession && (
          <StreamShareModal
            isOpen={showStreamShareModal}
            rtmpUrl={streamSession.custom_rtmp_url || streamSession.rtmp_url}
            streamKey={streamSession.custom_stream_key || streamSession.stream_key}
            srtUrl={streamSession.srt_url}
            srtStreamId={streamSession.srt_stream_id}
            courtName={game?.court_name || game?.location || `${game?.home_team_name} vs ${game?.away_team_name}`}
            isLive={streamSession.status === 'active'}
            onClose={() => setShowStreamShareModal(false)}
          />
        )}
        {showFinalConfirm && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-sm p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">End Game?</h3>
              <p className="text-sm text-zinc-400 mb-6">Score will be locked and game marked as final.</p>
              <div className="flex gap-3">
                <Button onClick={() => setShowFinalConfirm(false)} variant="outline" className="flex-1 border-zinc-600">Cancel</Button>
                <Button onClick={() => { updateStatus('final'); setShowFinalConfirm(false); }} className="flex-1 bg-red-600 hover:bg-red-500">Confirm End</Button>
              </div>
            </div>
          </div>
        )}
        {showAddPlayer && (
          <div className="fixed inset-0 bg-black/90 flex items-end justify-center z-50 p-3">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-sm p-4">
              <h3 className="text-sm font-bold text-white mb-2">Roster — {showAddPlayer === 'home' ? game.home_team_name : game.away_team_name}</h3>
              <p className="text-[10px] text-zinc-500 mb-2">One player per line: # Name (e.g. "23 Marcus Johnson")</p>
              <textarea
                value={bulkRosterText}
                onChange={e => setBulkRosterText(e.target.value)}
                rows={8}
                className="w-full bg-zinc-800 border border-zinc-700 text-white text-xs rounded-lg p-2 mb-3 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono"
                placeholder={"1 Player Name\n2 Player Name\n3 Player Name"}
                data-testid="mobile-bulk-roster-input"
              />
              <div className="flex gap-2">
                <Button onClick={() => { setShowAddPlayer(null); setBulkRosterText(''); }} variant="outline" className="flex-1 border-zinc-600">Cancel</Button>
                <Button onClick={() => saveBulkRoster(showAddPlayer)} className="flex-1 bg-emerald-600" data-testid="mobile-save-roster-btn">Save Roster</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col select-none" data-testid="operator-console">
      {/* ===== COMPACT HEADER WITH LOGO ===== */}
      <div className="bg-zinc-900 border-b-2 border-zinc-800 px-3 py-2 safe-area-top">
        <div className="flex items-center justify-between">
          {/* Back + Logo + Status */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/operator')} className="p-2 hover:bg-zinc-800 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <img 
              src="/rooam-logo.png" 
              alt="Rooam Sports" 
              className="h-8 w-auto hidden sm:block"
            />
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500' : 'bg-red-500'}`} />
          </div>

          {/* Scoreboard */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm md:text-base font-medium text-zinc-400 truncate max-w-[80px] md:max-w-[140px]">{game.home_team_name}</div>
              <div className="text-xs text-zinc-500">F: {calculateTeamFouls(homeRoster)} | TO: {Math.max(0, game.home_timeouts_remaining ?? 5)}</div>
            </div>
            <div 
              className="text-4xl font-black tabular-nums px-3"
              style={{ color: game.home_team_color || '#3B82F6' }}
            >
              {game.score?.home || 0}
            </div>
            
            <div className="text-center px-2">
              <div className="text-2xl font-mono font-bold">{formatTime(displayTime)}</div>
              <div className={`text-xs ${game.clock?.is_running ? 'text-emerald-400' : 'text-amber-400'}`}>
                {game.clock?.is_running ? '▶' : '⏸'} P{game.clock?.period || 1}
              </div>
            </div>
            
            <div 
              className="text-4xl font-black tabular-nums px-3"
              style={{ color: game.away_team_color || '#EF4444' }}
            >
              {game.score?.away || 0}
            </div>
            <div className="text-left">
              <div className="text-sm md:text-base font-medium text-zinc-400 truncate max-w-[80px] md:max-w-[140px]">{game.away_team_name}</div>
              <div className="text-xs text-zinc-500">F: {calculateTeamFouls(awayRoster)} | TO: {Math.max(0, game.away_timeouts_remaining ?? 5)}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              onClick={() => {
                navigator.clipboard?.writeText(gameId);
                toast.success('Game ID copied!');
              }}
              className="hidden md:flex items-center gap-1 px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-[10px] text-zinc-500 font-mono"
              title="Click to copy Game ID"
            >
              ID: {gameId.slice(0, 8)}...
            </button>
            <button 
              onClick={() => {
                setHomeTeamNameInput(game.home_team_name || '');
                setAwayTeamNameInput(game.away_team_name || '');
                setHomeTeamColorInput(game.home_team_color || '#3B82F6');
                setAwayTeamColorInput(game.away_team_color || '#EF4444');
                setShowTeamSettings(true);
              }}
              className="p-3 bg-zinc-700 hover:bg-zinc-600 rounded-xl active:scale-95 transition-all"
              title="Edit Teams"
              data-testid="edit-teams-btn"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button onClick={undoLastStat} className="p-3 bg-amber-600 hover:bg-amber-500 rounded-xl active:scale-95 transition-all" data-testid="undo-btn">
              <Undo2 className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setShowStreamSetup(true)}
              className={`p-3 rounded-xl active:scale-95 transition-all ${streamSession?.status === 'active' ? 'bg-red-600 animate-pulse' : 'bg-zinc-700 hover:bg-zinc-600'}`}
              title="Stream Settings"
              data-testid="stream-settings-btn"
            >
              <Video className="w-5 h-5" />
            </button>
            {streamSession && (
              <button 
                onClick={() => setShowStreamShareModal(true)}
                className="p-3 rounded-xl bg-cyan-700 hover:bg-cyan-600 active:scale-95 transition-all"
                title="Share Stream Key (QR Code)"
                data-testid="share-stream-qr-btn"
              >
                <QrCode className="w-5 h-5" />
              </button>
            )}
            <button 
              onClick={() => setForceMobile(true)}
              className="p-3 rounded-xl bg-zinc-700 hover:bg-zinc-600 active:scale-95 transition-all"
              title="Switch to Mobile View"
              data-testid="switch-to-mobile-btn"
            >
              <Smartphone className="w-5 h-5" />
            </button>
            {streamSession && (
              <button 
                onClick={() => setShowPerGamePreview(!showPerGamePreview)}
                className={`p-3 rounded-xl active:scale-95 transition-all ${
                  showPerGamePreview 
                    ? 'bg-emerald-700 hover:bg-emerald-600' 
                    : 'bg-zinc-700 hover:bg-zinc-600'
                }`}
                title={showPerGamePreview ? 'Hide Camera Preview' : 'Show Camera Preview'}
                data-testid="toggle-per-game-preview"
              >
                {showPerGamePreview ? <CameraOff className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
              </button>
            )}
            <button 
              onClick={() => setShowOverlaySettings(true)}
              className="p-3 rounded-xl bg-purple-600 hover:bg-purple-500 active:scale-95 transition-all"
              title="Overlay Settings"
              data-testid="overlay-settings-btn"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button 
              onClick={toggleFreePreview}
              className={`p-3 rounded-xl active:scale-95 transition-all ${freePreview ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-zinc-700 hover:bg-zinc-600'}`}
              title={freePreview ? 'Free Preview ON — viewers can watch without signup' : 'Free Preview OFF — 60s preview gate active'}
              data-testid="toggle-free-preview-btn"
            >
              {freePreview ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ===== CLOCK CONTROLS BAR ===== */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 px-3 py-2 flex items-center gap-2 overflow-x-auto">
        <Button
          onClick={game.clock?.is_running ? stopClock : startClock}
          className={`h-10 px-4 shrink-0 ${game.clock?.is_running ? 'bg-amber-600' : 'bg-emerald-600'}`}
        >
          {game.clock?.is_running ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
          {game.clock?.is_running ? 'Stop' : 'Start'}
        </Button>
        
        <Button onClick={nextPeriod} className="h-10 px-3 bg-blue-600 shrink-0">
          <ChevronRight className="w-4 h-4 mr-1" /> Next P
        </Button>
        
        <Button onClick={() => setShowClockSettings(true)} variant="outline" className="h-10 px-3 border-zinc-600 shrink-0">
          <Settings className="w-4 h-4" />
        </Button>

        <div className="h-5 w-px bg-zinc-700 mx-1" />

        <Button 
          onClick={() => callTimeout('home')} 
          className="h-10 px-3 shrink-0 text-white text-xs font-semibold"
          style={{ backgroundColor: game.home_team_color || '#3B82F6' }}
          disabled={Math.max(0, game.home_timeouts_remaining ?? 5) <= 0}
        >
          <Clock className="w-3 h-3 mr-1" /> 
          Home TO ({Math.max(0, game.home_timeouts_remaining ?? 5)})
        </Button>
        
        <Button 
          onClick={() => callTimeout('away')} 
          className="h-10 px-3 shrink-0 text-white text-xs font-semibold"
          style={{ backgroundColor: game.away_team_color || '#EF4444' }}
          disabled={Math.max(0, game.away_timeouts_remaining ?? 5) <= 0}
        >
          <Clock className="w-3 h-3 mr-1" /> 
          Away TO ({Math.max(0, game.away_timeouts_remaining ?? 5)})
        </Button>

        <div className="h-5 w-px bg-zinc-700 mx-1" />
        
        <Button 
          onClick={() => { setLocationInput(game.location || ''); setShowLocationSettings(true); }}
          variant="outline" 
          className="h-10 px-3 border-zinc-600 shrink-0 text-xs"
        >
          <MapPin className="w-3 h-3 mr-1" />
          {game.location ? game.location.substring(0, 12) + (game.location.length > 12 ? '..' : '') : 'Location'}
        </Button>
      </div>

      {/* ===== STATUS BAR — always visible, End Game pinned ===== */}
      <div className="bg-zinc-950 border-b border-zinc-800 px-3 py-1.5 flex items-center gap-2">
        {['scheduled', 'warmup', 'live', 'halftime'].map(status => (
          <button
            key={status}
            onClick={() => {
              if (isGameFinal) return;
              updateStatus(status);
            }}
            disabled={isGameFinal}
            data-testid={`status-btn-${status}`}
            className={`h-8 px-3 text-xs capitalize rounded-md font-semibold transition-all shrink-0 ${
              game.status === status 
                ? status === 'live' ? 'bg-red-600 text-white' 
                  : status === 'warmup' ? 'bg-amber-600 text-white'
                  : 'bg-blue-600 text-white'
                : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700'
            } ${isGameFinal ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            {status === 'warmup' && game.status === 'warmup' && (
              <span className="inline-block w-1.5 h-1.5 bg-amber-300 rounded-full mr-1.5 animate-pulse" />
            )}
            {status === 'live' && game.status === 'live' && (
              <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />
            )}
            {status}
          </button>
        ))}
        
        <div className="flex-1" />
        
        {/* End Game — always pinned to the right */}
        {!isGameFinal && (
          <button
            onClick={() => setShowFinalConfirm(true)}
            data-testid="end-game-btn"
            className="h-8 px-4 text-xs font-bold rounded-md bg-red-900/50 text-red-400 border border-red-700/50 hover:bg-red-800/60 transition-all shrink-0"
          >
            End Game
          </button>
        )}
        {isGameFinal && (
          <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Game Final</span>
        )}
      </div>

      {/* ===== PER-GAME CAMERA PREVIEW ===== */}
      {showPerGamePreview && streamSession && (
        <div className="px-3 py-2 border-b border-zinc-800">
          <CameraPreview
            playbackUrl={streamSession.playback_url || streamSession.hls_url}
            courtName={game?.location || `${game?.home_team_name} vs ${game?.away_team_name}`}
            onClose={() => setShowPerGamePreview(false)}
          />
        </div>
      )}

      {/* ===== COURT STREAM PANEL (Tournament Mode) ===== */}
      {game.event_id && (
        <div className="px-3 py-2 border-b border-zinc-800">
          <CourtStreamPanel
            gameId={gameId}
            eventId={game.event_id}
            token={token}
            onGameActivated={fetchGame}
            onGameEnded={fetchGame}
          />
        </div>
      )}

      {/* ===== PER-GAME STREAM PANEL (Non-event games) ===== */}
      {!game.event_id && (
        <div className="px-3 py-2 border-b border-zinc-800">
          <GameStreamInlinePanel
            streamSession={streamSession}
            onCreateSession={createStreamSession}
            onSetupClick={() => setShowStreamSetup(true)}
            onShareClick={() => setShowStreamShareModal(true)}
            gameId={gameId}
            gameName={`${game.home_team_name} vs ${game.away_team_name}`}
          />
        </div>
      )}

      {/* ===== DUAL-TEAM SCORING CONSOLE ===== */}
      <div className="flex-1 overflow-hidden flex flex-col relative" data-testid="scoring-console">
        {/* Game Final Lock Overlay */}
        {isGameFinal && (
          <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-[2px] z-30 flex items-center justify-center" data-testid="game-locked-overlay">
            <div className="text-center p-6">
              <Shield className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-zinc-400">Game Finalized</h3>
              <p className="text-sm text-zinc-600 mt-1">Score is locked. No further changes allowed.</p>
            </div>
          </div>
        )}

        {/* Mode Toggle + Undo */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-900/80 shrink-0">
          <div className="flex rounded-lg bg-zinc-800 p-0.5">
            <button
              onClick={() => setScoringMode('quick')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                scoringMode === 'quick' ? 'bg-emerald-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid="scoring-mode-quick"
            >
              Quick Score
            </button>
            <button
              onClick={() => setScoringMode('detailed')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${
                scoringMode === 'detailed' ? 'bg-blue-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid="scoring-mode-detailed"
            >
              Player Stats
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={undoLastStat}
              disabled={isGameFinal}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-700/30 text-amber-400 border border-amber-700/40 hover:bg-amber-700/50 disabled:opacity-30 transition-all"
              data-testid="undo-last-play"
            >
              Undo Last
            </button>
            <button
              onClick={() => setActiveTab(activeTab === 'log' ? 'home' : 'log')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                activeTab === 'log' ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
              }`}
              data-testid="toggle-game-log"
            >
              Game Log
            </button>
          </div>
        </div>

        {activeTab === 'log' ? (
          /* ===== GAME LOG VIEW ===== */
          <div className="flex-1 overflow-y-auto divide-y divide-zinc-800">
            {playByPlay.length === 0 ? (
              <div className="text-center py-16 text-zinc-500">No plays logged yet</div>
            ) : (
              [...playByPlay].reverse().map((play, idx) => (
                <div key={play.seq} className={`px-4 py-3 flex items-center gap-3 ${idx === 0 ? 'bg-zinc-800/50' : ''}`}>
                  <div 
                    className="w-1.5 h-10 rounded-full shrink-0"
                    style={{ backgroundColor: play.team_side === 'home' ? (game.home_team_color || '#3B82F6') : (game.away_team_color || '#EF4444') }}
                  />
                  <div className="text-xs text-zinc-500 font-mono w-8">#{play.seq}</div>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    play.event_subtype?.includes('MADE') ? 'bg-emerald-900/50 text-emerald-400' :
                    play.event_subtype?.includes('MISS') ? 'bg-zinc-700 text-zinc-400' :
                    play.event_subtype === 'FOUL' ? 'bg-red-900/50 text-red-400' :
                    play.event_subtype === 'UNDO' ? 'bg-amber-900/50 text-amber-400' :
                    'bg-blue-900/50 text-blue-400'
                  }`}>
                    {play.event_subtype}
                  </span>
                  {play.player_number && <span className="text-zinc-300">#{play.player_number}</span>}
                  <span className="text-zinc-400 truncate">{play.player_name}</span>
                  <span className="ml-auto text-xs text-zinc-600">P{play.clock?.period || play.period}</span>
                </div>
              ))
            )}
          </div>
        ) : scoringMode === 'quick' ? (
          /* ===== QUICK SCORE — DUAL TEAM LAYOUT ===== */
          <div className="flex-1 overflow-y-auto" data-testid="quick-score-dual">
            <div className="grid grid-cols-[1fr_auto_1fr] min-h-full">
              {/* HOME TEAM COLUMN */}
              <div className="border-r border-zinc-800 flex flex-col" style={{ borderTopColor: game.home_team_color || '#3B82F6' }} data-testid="home-score-column">
                {/* Score Header — click to edit */}
                <div onClick={openScoreEdit} className="p-3 border-b border-zinc-800 text-center cursor-pointer hover:bg-zinc-800/30 transition-colors" style={{ background: `linear-gradient(135deg, ${game.home_team_color || '#3B82F6'}15, transparent)` }}>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{game.home_team_name}</p>
                  <p className="text-5xl md:text-6xl font-black text-white tabular-nums leading-none" data-testid="home-score-display">{game.score?.home || 0}</p>
                  <div className="flex justify-center gap-6 mt-2 text-xs">
                    <span className="text-zinc-400">{game.fouls?.home || 0} <span style={{ color: game.home_team_color || '#3B82F6' }}>Fouls</span></span>
                    <span className="text-zinc-400">{game.timeouts?.home ?? 5} <span style={{ color: game.home_team_color || '#3B82F6' }}>Timeouts</span></span>
                  </div>
                  <p className="text-[8px] text-zinc-600 mt-1">CLICK TO EDIT SCORE</p>
                </div>

                {/* Scoring Buttons */}
                <div className="p-3 grid grid-cols-3 gap-2">
                  <button
                    onClick={() => logTeamStat('ft', 'home')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="home-ft-btn"
                  >
                    FT
                  </button>
                  <button
                    onClick={() => logTeamStat('fg', 'home')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="home-2pt-btn"
                  >
                    +2
                  </button>
                  <button
                    onClick={() => logTeamStat('fg3', 'home')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="home-3pt-btn"
                  >
                    +3
                  </button>
                </div>

                {/* Foul + Timeout */}
                <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => logTeamStat('foul', 'home')}
                    disabled={isGameFinal}
                    className="h-12 md:h-14 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-sm font-bold transition-all border border-zinc-700 flex items-center justify-center gap-2 disabled:opacity-30"
                    data-testid="home-foul-btn"
                  >
                    <span className="text-base">&#9995;</span> FOUL
                  </button>
                  <button
                    onClick={() => callTimeout('home')}
                    disabled={isGameFinal}
                    className="h-12 md:h-14 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-sm font-bold transition-all border border-zinc-700 flex items-center justify-center gap-2 disabled:opacity-30"
                    data-testid="home-timeout-btn"
                  >
                    <Pause className="w-4 h-4" /> TIMEOUT
                  </button>
                </div>

                {/* Home Roster */}
                <div className="border-t border-zinc-800 flex-1 overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10">
                    <span className="text-xs font-bold text-zinc-400">Roster {homeRoster.length}</span>
                    <button onClick={() => openBulkRoster('home')} className="text-[10px] font-semibold text-emerald-400 hover:text-emerald-300" data-testid="home-add-player">Edit Roster</button>
                  </div>
                  <div className="px-2 pb-2">
                    <table className="w-full text-xs">
                      <thead><tr className="text-zinc-600 text-[10px]"><th className="text-left pl-2 w-8">#</th><th className="text-left">Name</th><th className="text-center w-10">Pts</th><th className="text-center w-10">Fls</th></tr></thead>
                      <tbody>
                        {homeRoster.map(p => (
                          <tr key={p.player_id} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="pl-2 py-1.5 text-zinc-500 font-mono">{p.jersey_number}</td>
                            <td className="py-1.5 text-white truncate max-w-[80px]">{p.name}</td>
                            <td className="text-center text-zinc-300 tabular-nums">{p.stats?.points || 0}</td>
                            <td className="text-center text-zinc-300 tabular-nums">{p.stats?.fouls || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* CENTER COLUMN — Clock, Period, Game Log */}
              <div className="w-[180px] md:w-[220px] flex flex-col border-r border-zinc-800 bg-zinc-950/50">
                {/* Venue & Period */}
                <div className="p-3 border-b border-zinc-800 text-center">
                  {game.location && (
                    <p className="text-[10px] text-zinc-500 truncate mb-1">{game.location}</p>
                  )}
                  <div className="inline-flex items-center gap-1 text-xs text-zinc-300 bg-zinc-800 rounded-full px-3 py-1 mb-2">
                    {game.clock?.period === 1 ? '1st Half' : game.clock?.period === 2 ? '2nd Half' : `P${game.clock?.period || 1}`}
                  </div>
                  <p className="text-3xl md:text-4xl font-mono font-black text-white tabular-nums" data-testid="center-clock">
                    {formatTime(game.clock?.time_remaining_ms)}
                  </p>
                </div>

                {/* Mini Play-by-Play Feed */}
                <div className="flex-1 overflow-y-auto">
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-zinc-600 font-semibold sticky top-0 bg-zinc-950/95">Game Log</p>
                  {playByPlay.length === 0 ? (
                    <div className="text-center py-6 text-zinc-600 text-xs">No plays yet</div>
                  ) : (
                    [...playByPlay].reverse().slice(0, 20).map((play) => (
                      <div key={play.seq} className="px-2 py-1.5 border-b border-zinc-800/40 flex gap-1.5 items-start">
                        <div 
                          className="w-1 h-4 rounded-full shrink-0 mt-0.5"
                          style={{ backgroundColor: play.team_side === 'home' ? (game.home_team_color || '#3B82F6') : (game.away_team_color || '#EF4444') }}
                        />
                        <div className="min-w-0">
                          <div className={`text-[10px] font-bold ${
                            play.event_subtype?.includes('MADE') || play.event_subtype === 'FT_MADE' ? 'text-emerald-400' :
                            play.event_subtype?.includes('MISS') ? 'text-zinc-500' :
                            play.event_subtype === 'FOUL' ? 'text-red-400' :
                            play.event_subtype === 'UNDO' ? 'text-amber-400' :
                            'text-cyan-400'
                          }`}>
                            {play.event_subtype}
                          </div>
                          <div className="text-[9px] text-zinc-500 truncate">
                            {play.team_side === 'home' ? game.home_team_name : game.away_team_name}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* AWAY TEAM COLUMN */}
              <div className="flex flex-col" style={{ borderTopColor: game.away_team_color || '#EF4444' }} data-testid="away-score-column">
                {/* Score Header — click to edit */}
                <div onClick={openScoreEdit} className="p-3 border-b border-zinc-800 text-center cursor-pointer hover:bg-zinc-800/30 transition-colors" style={{ background: `linear-gradient(225deg, ${game.away_team_color || '#EF4444'}15, transparent)` }}>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{game.away_team_name}</p>
                  <p className="text-5xl md:text-6xl font-black text-white tabular-nums leading-none" data-testid="away-score-display">{game.score?.away || 0}</p>
                  <div className="flex justify-center gap-6 mt-2 text-xs">
                    <span className="text-zinc-400">{game.fouls?.away || 0} <span style={{ color: game.away_team_color || '#EF4444' }}>Fouls</span></span>
                    <span className="text-zinc-400">{game.timeouts?.away ?? 5} <span style={{ color: game.away_team_color || '#EF4444' }}>Timeouts</span></span>
                  </div>
                  <p className="text-[8px] text-zinc-600 mt-1">CLICK TO EDIT SCORE</p>
                </div>

                {/* Scoring Buttons */}
                <div className="p-3 grid grid-cols-3 gap-2">
                  <button
                    onClick={() => logTeamStat('ft', 'away')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="away-ft-btn"
                  >
                    FT
                  </button>
                  <button
                    onClick={() => logTeamStat('fg', 'away')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="away-2pt-btn"
                  >
                    +2
                  </button>
                  <button
                    onClick={() => logTeamStat('fg3', 'away')}
                    disabled={isGameFinal}
                    className="h-14 md:h-16 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-lg md:text-xl font-black transition-all border border-zinc-700 disabled:opacity-30"
                    data-testid="away-3pt-btn"
                  >
                    +3
                  </button>
                </div>

                {/* Foul + Timeout */}
                <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => logTeamStat('foul', 'away')}
                    disabled={isGameFinal}
                    className="h-12 md:h-14 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-sm font-bold transition-all border border-zinc-700 flex items-center justify-center gap-2 disabled:opacity-30"
                    data-testid="away-foul-btn"
                  >
                    <span className="text-base">&#9995;</span> FOUL
                  </button>
                  <button
                    onClick={() => callTimeout('away')}
                    disabled={isGameFinal}
                    className="h-12 md:h-14 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white text-sm font-bold transition-all border border-zinc-700 flex items-center justify-center gap-2 disabled:opacity-30"
                    data-testid="away-timeout-btn"
                  >
                    <Pause className="w-4 h-4" /> TIMEOUT
                  </button>
                </div>

                {/* Away Roster */}
                <div className="border-t border-zinc-800 flex-1 overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10">
                    <span className="text-xs font-bold text-zinc-400">Roster {awayRoster.length}</span>
                    <button onClick={() => openBulkRoster('away')} className="text-[10px] font-semibold text-emerald-400 hover:text-emerald-300" data-testid="away-add-player">Edit Roster</button>
                  </div>
                  <div className="px-2 pb-2">
                    <table className="w-full text-xs">
                      <thead><tr className="text-zinc-600 text-[10px]"><th className="text-left pl-2 w-8">#</th><th className="text-left">Name</th><th className="text-center w-10">Pts</th><th className="text-center w-10">Fls</th></tr></thead>
                      <tbody>
                        {awayRoster.map(p => (
                          <tr key={p.player_id} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="pl-2 py-1.5 text-zinc-500 font-mono">{p.jersey_number}</td>
                            <td className="py-1.5 text-white truncate max-w-[80px]">{p.name}</td>
                            <td className="text-center text-zinc-300 tabular-nums">{p.stats?.points || 0}</td>
                            <td className="text-center text-zinc-300 tabular-nums">{p.stats?.fouls || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ===== PLAYER STATS MODE — Side-by-Side Rosters ===== */
          <div className="flex-1 overflow-y-auto" data-testid="player-stats-dual">
            <div className="grid grid-cols-2 divide-x divide-zinc-800 min-h-full">
              {/* Home Roster */}
              <div className="flex flex-col">
                <div className="p-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10">
                  <div>
                    <p className="text-sm font-bold text-white">{game.home_team_name}</p>
                    <p className="text-lg font-black tabular-nums" style={{ color: game.home_team_color || '#3B82F6' }}>{game.score?.home || 0}</p>
                  </div>
                  <Button
                    onClick={() => openBulkRoster('home')}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs"
                    data-testid="home-add-player-detailed"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Edit Roster
                  </Button>
                </div>
                {homeRoster.length === 0 ? (
                  <div className="text-center py-8 text-zinc-600 text-sm">No players added</div>
                ) : (
                  homeRoster.map(player => (
                    <PlayerRow
                      key={player.player_id}
                      player={player}
                      teamSide="home"
                      teamColor={game.home_team_color || '#3B82F6'}
                      isSelected={selectedPlayer?.player_id === player.player_id}
                      onSelect={(p, side) => { setSelectedPlayer(p); setSelectedTeamSide(side); }}
                      onEdit={(p, side) => { setEditingPlayer(p); setEditingTeamSide(side); }}
                      onToggleCourt={togglePlayerCourt}
                    />
                  ))
                )}
              </div>
              {/* Away Roster */}
              <div className="flex flex-col">
                <div className="p-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10">
                  <div>
                    <p className="text-sm font-bold text-white">{game.away_team_name}</p>
                    <p className="text-lg font-black tabular-nums" style={{ color: game.away_team_color || '#EF4444' }}>{game.score?.away || 0}</p>
                  </div>
                  <Button
                    onClick={() => openBulkRoster('away')}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs"
                    data-testid="away-add-player-detailed"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Edit Roster
                  </Button>
                </div>
                {awayRoster.length === 0 ? (
                  <div className="text-center py-8 text-zinc-600 text-sm">No players added</div>
                ) : (
                  awayRoster.map(player => (
                    <PlayerRow
                      key={player.player_id}
                      player={player}
                      teamSide="away"
                      teamColor={game.away_team_color || '#EF4444'}
                      isSelected={selectedPlayer?.player_id === player.player_id}
                      onSelect={(p, side) => { setSelectedPlayer(p); setSelectedTeamSide(side); }}
                      onEdit={(p, side) => { setEditingPlayer(p); setEditingTeamSide(side); }}
                      onToggleCourt={togglePlayerCourt}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== MODALS ===== */}
      
      {selectedPlayer && (
        <StatModal
          player={selectedPlayer}
          teamSide={selectedTeamSide}
          teamColor={selectedTeamSide === 'home' ? game.home_team_color : game.away_team_color}
          onLogStat={logStat}
          onClose={() => { setSelectedPlayer(null); setSelectedTeamSide(null); }}
        />
      )}

      {editingPlayer && (
        <EditPlayerModal
          player={editingPlayer}
          teamSide={editingTeamSide}
          onSave={savePlayer}
          onDelete={deletePlayer}
          onClose={() => { setEditingPlayer(null); setEditingTeamSide(null); }}
        />
      )}

      {showStreamSetup && (
        <StreamSettingsModal
          streamSession={streamSession}
          onCreateSession={createStreamSession}
          onEndSession={endStreamSession}
          onClose={() => setShowStreamSetup(false)}
          copyToClipboard={copyToClipboard}
          gameId={gameId}
          authHeaders={authHeaders}
          onSessionUpdate={(session) => {
            setStreamSession(session);
            // Also refresh game data so all panels stay in sync
            fetchGame();
          }}
        />
      )}

      {showStreamShareModal && streamSession && (
        <StreamShareModal
          rtmpUrl={streamSession.custom_rtmp_url || streamSession.rtmp_url}
          streamKey={streamSession.custom_stream_key || streamSession.stream_key}
          srtUrl={streamSession.srt_url}
          srtStreamId={streamSession.srt_stream_id}
          courtName={game?.location || `${game?.home_team_name} vs ${game?.away_team_name}`}
          isLive={streamSession.status === 'active'}
          onClose={() => setShowStreamShareModal(false)}
        />
      )}

      {/* Score Edit Dialog */}
      {showScoreEdit && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-sm">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Set Score</h2>
              <button onClick={() => setShowScoreEdit(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-xs text-zinc-500 uppercase mb-2 truncate">{game.home_team_name}</p>
                  <input
                    type="number"
                    min="0"
                    value={editHomeScore}
                    onChange={(e) => setEditHomeScore(e.target.value)}
                    className="w-full bg-zinc-800 border-2 border-zinc-600 text-white text-4xl font-black h-20 text-center rounded-xl focus:outline-none focus:border-cyan-500"
                    data-testid="edit-home-score-input"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-zinc-500 uppercase mb-2 truncate">{game.away_team_name}</p>
                  <input
                    type="number"
                    min="0"
                    value={editAwayScore}
                    onChange={(e) => setEditAwayScore(e.target.value)}
                    className="w-full bg-zinc-800 border-2 border-zinc-600 text-white text-4xl font-black h-20 text-center rounded-xl focus:outline-none focus:border-cyan-500"
                    data-testid="edit-away-score-input"
                  />
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 text-center">This will adjust the score via a correction event in the game log.</p>
              <div className="flex gap-3">
                <Button onClick={() => setShowScoreEdit(false)} variant="outline" className="flex-1 border-zinc-600 text-zinc-300">Cancel</Button>
                <Button onClick={saveScoreEdit} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white font-bold" data-testid="save-score-edit-btn">Set Score</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddPlayer && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Roster — {showAddPlayer === 'home' ? game.home_team_name : game.away_team_name}</h2>
              <button onClick={() => setShowAddPlayer(null)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-6 h-6 text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-zinc-500">One player per line: # Name (e.g. "23 Marcus Johnson")</p>
              <textarea
                value={bulkRosterText}
                onChange={(e) => setBulkRosterText(e.target.value)}
                rows={10}
                className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono"
                placeholder={"1 Player Name\n2 Player Name\n3 Player Name"}
                data-testid="desktop-bulk-roster-input"
              />
              <Button onClick={() => saveBulkRoster(showAddPlayer)} className="w-full h-14 text-lg bg-emerald-600" data-testid="desktop-save-roster-btn">
                <Plus className="w-5 h-5 mr-2" /> Save Roster
              </Button>
            </div>
          </div>
        </div>
      )}

      {showClockSettings && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-3">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md shadow-2xl max-h-[92vh] flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between shrink-0">
              <h2 className="text-base font-bold text-white">Clock & Period Settings</h2>
              <button onClick={() => setShowClockSettings(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Period Controls */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Period</label>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    onClick={async () => {
                      const newPeriod = Math.max(1, (game.clock?.period || 1) - 1);
                      await fetch(`${API_URL}/api/engine/games/${gameId}/clock/set`, {
                        method: 'POST',
                        headers: authHeaders,
                        body: JSON.stringify({ period: newPeriod })
                      });
                    }}
                    className="h-11 w-11 bg-zinc-700 hover:bg-zinc-600 text-xl font-bold"
                    disabled={(game.clock?.period || 1) <= 1}
                  >
                    −
                  </Button>
                  <div className="text-4xl font-bold text-white w-16 text-center tabular-nums">
                    {game.clock?.period || 1}
                  </div>
                  <Button
                    onClick={async () => {
                      const newPeriod = (game.clock?.period || 1) + 1;
                      await fetch(`${API_URL}/api/engine/games/${gameId}/clock/set`, {
                        method: 'POST',
                        headers: authHeaders,
                        body: JSON.stringify({ period: newPeriod })
                      });
                    }}
                    className="h-11 w-11 bg-zinc-700 hover:bg-zinc-600 text-xl font-bold"
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="border-t border-zinc-700" />

              {/* Clock Time */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Set Clock Time</label>
                <Input
                  value={clockInput}
                  onChange={(e) => setClockInput(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white text-2xl h-12 text-center font-mono"
                  placeholder="MM:SS"
                />
                <p className="text-[11px] text-zinc-500 text-center mt-1">Current: {formatTime(game.clock?.time_remaining_ms)}</p>
              </div>

              {/* Quick Set Buttons */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Quick Set</label>
                <div className="grid grid-cols-4 gap-2">
                  {['8:00', '6:00', '4:00', '2:00'].map(time => (
                    <Button
                      key={time}
                      onClick={async () => {
                        const ms = parseTime(time);
                        await fetch(`${API_URL}/api/engine/games/${gameId}/clock/set`, {
                          method: 'POST',
                          headers: authHeaders,
                          body: JSON.stringify({ time_remaining_ms: ms })
                        });
                      }}
                      variant="outline"
                      className="h-10 border-zinc-600 text-zinc-300 hover:bg-zinc-800 font-mono text-sm"
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t border-zinc-700" />

              {/* Timeouts Settings */}
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-2">Timeouts Per Team</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">Home ({game.home_team_name})</label>
                    <div className="flex items-center gap-1.5">
                      <Button
                        onClick={async () => {
                          const newVal = Math.max(0, (game.home_timeouts_remaining ?? 5) - 1);
                          await fetch(`${API_URL}/api/engine/games/${gameId}`, {
                            method: 'PUT',
                            headers: authHeaders,
                            body: JSON.stringify({ home_timeouts_remaining: newVal })
                          });
                          fetchGame();
                        }}
                        className="h-10 w-10 bg-zinc-700 hover:bg-zinc-600 text-base"
                      >−</Button>
                      <div className="text-xl font-bold text-white w-8 text-center">
                        {Math.max(0, game.home_timeouts_remaining ?? 5)}
                      </div>
                      <Button
                        onClick={async () => {
                          const newVal = (game.home_timeouts_remaining ?? 5) + 1;
                          await fetch(`${API_URL}/api/engine/games/${gameId}`, {
                            method: 'PUT',
                            headers: authHeaders,
                            body: JSON.stringify({ home_timeouts_remaining: newVal })
                          });
                          fetchGame();
                        }}
                        className="h-10 w-10 bg-zinc-700 hover:bg-zinc-600 text-base"
                      >+</Button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">Away ({game.away_team_name})</label>
                    <div className="flex items-center gap-1.5">
                      <Button
                        onClick={async () => {
                          const newVal = Math.max(0, (game.away_timeouts_remaining ?? 5) - 1);
                          await fetch(`${API_URL}/api/engine/games/${gameId}`, {
                            method: 'PUT',
                            headers: authHeaders,
                            body: JSON.stringify({ away_timeouts_remaining: newVal })
                          });
                          fetchGame();
                        }}
                        className="h-10 w-10 bg-zinc-700 hover:bg-zinc-600 text-base"
                      >−</Button>
                      <div className="text-xl font-bold text-white w-8 text-center">
                        {Math.max(0, game.away_timeouts_remaining ?? 5)}
                      </div>
                      <Button
                        onClick={async () => {
                          const newVal = (game.away_timeouts_remaining ?? 5) + 1;
                          await fetch(`${API_URL}/api/engine/games/${gameId}`, {
                            method: 'PUT',
                            headers: authHeaders,
                            body: JSON.stringify({ away_timeouts_remaining: newVal })
                          });
                          fetchGame();
                        }}
                        className="h-10 w-10 bg-zinc-700 hover:bg-zinc-600 text-base"
                      >+</Button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                  {[3, 4, 5, 6].map(n => (
                    <Button
                      key={n}
                      onClick={async () => {
                        await fetch(`${API_URL}/api/engine/games/${gameId}`, {
                          method: 'PUT',
                          headers: authHeaders,
                          body: JSON.stringify({ home_timeouts_remaining: n, away_timeouts_remaining: n })
                        });
                        fetchGame();
                        toast.success(`Both teams set to ${n} timeouts`);
                      }}
                      variant="outline"
                      className="h-7 px-2.5 border-zinc-600 text-[11px]"
                    >
                      Reset to {n}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Apply Button */}
              <Button 
                onClick={setClock} 
                className="w-full h-12 text-base bg-blue-600 hover:bg-blue-500"
                disabled={!clockInput}
              >
                Set Time
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Location Settings Modal */}
      {showLocationSettings && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Game Location
              </h2>
              <button onClick={() => setShowLocationSettings(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-6 h-6 text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Location</label>
                <Input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white text-lg h-14"
                  placeholder="e.g., Main Gym, Court 1"
                />
                <p className="text-xs text-zinc-500 mt-2">Enter the venue, gym, or court name</p>
              </div>

              <Button 
                onClick={updateLocation} 
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-500"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Location
              </Button>
            </div>
          </div>
        </div>
      )}

      {showTeamSettings && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                Edit Team Names
              </h2>
              <button onClick={() => setShowTeamSettings(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-6 h-6 text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Home Team */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: homeTeamColorInput || '#3B82F6' }}
                  />
                  <label className="text-sm font-semibold text-white">Home Team</label>
                </div>
                <Input
                  value={homeTeamNameInput}
                  onChange={(e) => setHomeTeamNameInput(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white text-lg h-12"
                  placeholder="Home Team Name"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500">Color:</label>
                  <input
                    type="color"
                    value={homeTeamColorInput || '#3B82F6'}
                    onChange={(e) => setHomeTeamColorInput(e.target.value)}
                    className="w-10 h-8 rounded cursor-pointer bg-transparent"
                  />
                  <span className="text-xs text-zinc-400 font-mono">{homeTeamColorInput || '#3B82F6'}</span>
                </div>
              </div>

              <div className="border-t border-zinc-700" />

              {/* Away Team */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: awayTeamColorInput || '#EF4444' }}
                  />
                  <label className="text-sm font-semibold text-white">Away Team</label>
                </div>
                <Input
                  value={awayTeamNameInput}
                  onChange={(e) => setAwayTeamNameInput(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white text-lg h-12"
                  placeholder="Away Team Name"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-zinc-500">Color:</label>
                  <input
                    type="color"
                    value={awayTeamColorInput || '#EF4444'}
                    onChange={(e) => setAwayTeamColorInput(e.target.value)}
                    className="w-10 h-8 rounded cursor-pointer bg-transparent"
                  />
                  <span className="text-xs text-zinc-400 font-mono">{awayTeamColorInput || '#EF4444'}</span>
                </div>
              </div>

              <Button 
                onClick={updateTeamSettings} 
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-500"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Team Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay Settings Modal */}
      {showOverlaySettings && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 w-full max-w-md">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-400" />
                Overlay Settings
              </h2>
              <button onClick={() => setShowOverlaySettings(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                <X className="w-6 h-6 text-zinc-400" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Watermark Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Image className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Show Watermark</p>
                    <p className="text-xs text-zinc-500">Display Rooam logo on video</p>
                  </div>
                </div>
                <Switch
                  checked={overlaySettings.showWatermark}
                  onCheckedChange={(checked) => setOverlaySettings({...overlaySettings, showWatermark: checked})}
                />
              </div>

              {/* Watermark Position */}
              {overlaySettings.showWatermark && (
                <div className="pl-12 space-y-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Position</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'top-left', label: 'Top Left' },
                        { value: 'top-right', label: 'Top Right' },
                        { value: 'bottom-left', label: 'Bottom Left' },
                        { value: 'bottom-right', label: 'Bottom Right' }
                      ].map(pos => (
                        <Button
                          key={pos.value}
                          variant={overlaySettings.watermarkPosition === pos.value ? 'default' : 'outline'}
                          size="sm"
                          className={overlaySettings.watermarkPosition === pos.value ? 'bg-purple-600' : 'border-zinc-600'}
                          onClick={() => setOverlaySettings({...overlaySettings, watermarkPosition: pos.value})}
                        >
                          {pos.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-2 block">Opacity: {Math.round(overlaySettings.watermarkOpacity * 100)}%</label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={overlaySettings.watermarkOpacity}
                      onChange={(e) => setOverlaySettings({...overlaySettings, watermarkOpacity: parseFloat(e.target.value)})}
                      className="w-full accent-purple-500"
                    />
                  </div>
                </div>
              )}

              <Button 
                onClick={saveOverlaySettings} 
                className="w-full h-14 text-lg bg-purple-600 hover:bg-purple-500"
              >
                <Save className="w-5 h-5 mr-2" />
                Save Overlay Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Final Confirmation Dialog */}
      {showFinalConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="final-confirm-dialog">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">End Game?</h3>
              <p className="text-sm text-zinc-400">
                Are you sure the final score is correct? Once finalized, <span className="text-white font-semibold">no more changes can be made</span>.
              </p>
              <div className="mt-4 bg-zinc-800 rounded-lg p-3 flex items-center justify-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-zinc-500">{game.home_team_name}</div>
                  <div className="text-2xl font-bold text-white">{game.score?.home || 0}</div>
                </div>
                <div className="text-zinc-600 font-bold">-</div>
                <div className="text-left">
                  <div className="text-xs text-zinc-500">{game.away_team_name}</div>
                  <div className="text-2xl font-bold text-white">{game.score?.away || 0}</div>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-12 border-zinc-600 text-zinc-300"
                onClick={() => setShowFinalConfirm(false)}
                data-testid="final-cancel-btn"
              >
                Go Back
              </Button>
              <Button
                className="flex-1 h-12 bg-red-600 hover:bg-red-500 text-white font-semibold"
                onClick={() => {
                  updateStatus('final');
                  setShowFinalConfirm(false);
                  toast.success('Game finalized. No further changes allowed.');
                }}
                data-testid="final-confirm-btn"
              >
                Finalize Game
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Game Final Lock Overlay — with Undo button */}
      {isGameFinal && (
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-700 py-3 px-4 z-40 safe-area-bottom" data-testid="game-final-banner">
          <div className="flex items-center justify-center gap-3">
            <Shield className="h-5 w-5 text-zinc-500" />
            <span className="text-sm font-semibold text-zinc-400">Game Final — Score Locked</span>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/engine/games/${gameId}/revert-final`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) }
                  });
                  if (res.ok) {
                    toast.success('Game reverted to LIVE — you can continue scoring');
                    fetchGame();
                  } else {
                    const err = await res.json();
                    toast.error(err.detail || 'Failed to revert');
                  }
                } catch { toast.error('Failed to revert game'); }
              }}
              className="ml-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-lg transition-colors"
              data-testid="revert-final-btn"
            >
              Undo Final
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
